import { useContext } from "react";
import { useHistory } from "react-router-dom";
import { EditorContext } from "./editor";

import {
  GrainsizeLayoutProvider,
  ColumnSVG,
  ColumnBox,
  FloodingSurface,
  TriangleBars,
  LithologyColumn,
  GeneralizedSectionColumn,
  SimplifiedLithologyColumn,
  CoveredOverlay,
  FaciesColumnInner,
  ColumnContext,
  ColumnAxis,
  DivisionEditOverlay,
  useSettings,
} from "@macrostrat/column-components";

import { ManagedSymbolColumn, ColumnTracker } from "../components";
import { SummaryColumnProvider } from "./data-provider";
import { PlatformContext } from "~/platform";
import { MinimalIsotopesColumn } from "./chemostrat";
import { FaciesTractIntervals } from "../column/facies-tracts";

import { hyperStyled } from "@macrostrat/hyper";
import styles from "./main.module.styl";
const h = hyperStyled(styles);

const ColumnMain = function () {
  const { showFacies, showFaciesTracts, showLithology, showGrainsize } =
    useSettings();
  let c = GeneralizedSectionColumn;
  let width = null;
  if (!showGrainsize) {
    c = LithologyColumn;
    width = 60;
  }

  return h(c, { width }, [
    h.if(showFacies)(FaciesColumnInner),
    h.if(showFaciesTracts)(FaciesTractIntervals),
    h.if(showLithology)(SimplifiedLithologyColumn),
    h(CoveredOverlay, {
      color: "var(--covered-interval-color, rgba(0,0,0,0.2))",
      patternSize: 9,
      strokeWidth: 4,
    }),
  ]);
};

const EditOverlay = function (props) {
  let navigateTo: (arg0: string) => void;
  let { interactive } = useSettings();
  if (interactive == null) {
    interactive = false;
  }
  if (!interactive) {
    return null;
  }
  try {
    navigateTo = useHistory()?.push;
  } catch (error) {
    navigateTo = () => {};
  }

  let { id, ...rest } = props;
  const { onEditInterval, editingInterval: interval } =
    useContext(EditorContext);
  const editingInterval = interval?.section_id == id ? interval : null;

  const onClick = function ({ height, event, division }) {
    const sectionID = division?.original_section ?? division?.section_id ?? id;
    // If we're working with a generalized division, we need to recalculate height
    let _height = height;
    if (division?.original_bottom != null) {
      _height -= division.bottom;
      _height += division.original_bottom;
    }

    if (event.shiftKey && onEditInterval != null) {
      onEditInterval(division);
      return;
    }

    // OR, navigate to a certain height
    let path = `/sections/${sectionID}`;
    if (_height != null) {
      // Sometimes, URLs have a problem with non-rounded heights
      path += `/height/${Math.round(_height)}`;
    }
    navigateTo(path);
  };

  return h(DivisionEditOverlay, {
    showInfoBox: false,
    onClick,
    editingInterval,
    ...rest,
  });
};

const ColumnSummaryAxis = function (props) {
  const { height, zoom, scale, pixelsPerMeter } = useContext(ColumnContext);
  const ratio = pixelsPerMeter * zoom;

  // Keep labels from inhabiting the top few pixels (to make space for section labels)
  return h(ColumnAxis, {
    ticks: (height * zoom) / 5,
  });
};

function ColumnIsotopes(props) {
  let {
    id,
    columnWidth = 40,
    showCarbonIsotopes = true,
    showOxygenIsotopes = true,
    translateLeft = 120,
  } = props;

  return h([
    h.if(showCarbonIsotopes)(MinimalIsotopesColumn, {
      width: columnWidth,
      section: id,
      transform: `translate(${translateLeft})`,
      system: "delta13c",
    }),
    h.if(showOxygenIsotopes)(MinimalIsotopesColumn, {
      width: columnWidth,
      section: id,
      transform: `translate(${translateLeft + columnWidth})`,
      system: "delta18o",
    }),
  ]);
}

const ColumnUnderlay = function (props) {
  let { width, paddingLeft } = props;
  const { pixelHeight } = useContext(ColumnContext);
  if (paddingLeft == null) {
    paddingLeft = 5;
  }
  return h("rect.underlay", {
    width,
    height: pixelHeight + 10,
    x: paddingLeft,
    y: -5,
    fill: "var(--background-color)",
  });
};

interface SVGSectionInnerProps {
  id: string;
  padding: {
    left: number;
    top: number;
    right: number;
    bottom: number;
  };
  innerWidth: number;
  showUnderlay: boolean;
  offsetTop: number | null;
  absolutePosition: boolean;
  isotopeColumnWidth?: number;
  axisComponent?: any;
  sequenceStratOrder?: [number, number] | null;
  showFloodingSurfaces?: boolean;
  showTriangleBars?: boolean;
  showCarbonIsotopes?: boolean;
  showOxygenIsotopes?: boolean;
  isotopesPerSection?: boolean;
  triangleBarRightSide?: boolean;
  children?: React.ReactNode;
  trackerOffset?: {
    x: number;
    y: number;
  };
}

const defaultPadding = {
  left: 30,
  top: 10,
  right: 20,
  bottom: 35,
};

function SVGSectionInner(props: SVGSectionInnerProps) {
  let {
    id,
    padding = defaultPadding,
    innerWidth = 100,
    showUnderlay = true,
    offsetTop,
    absolutePosition = true,
    isotopeColumnWidth = 60,
    axisComponent = ColumnSummaryAxis,
    sequenceStratOrder = [0, 1],
    showFloodingSurfaces = false,
    showTriangleBars = true,
    showCarbonIsotopes = true,
    showOxygenIsotopes = true,
    isotopesPerSection,
    triangleBarRightSide = false,
  } = props;

  const { inEditMode = false } = useContext(PlatformContext);

  let overallWidth = 120;
  const chemostratColumnStart = overallWidth;

  let underlayPaddingLeft = -padding.left;
  let underlayWidth = 210;

  overallWidth += 42; // Symbol column

  let chemostratWidth = 0;
  const chemostratColumnWidth = isotopeColumnWidth;
  if (isotopesPerSection) {
    if (showCarbonIsotopes) {
      chemostratWidth += chemostratColumnWidth;
    }
    if (showOxygenIsotopes) {
      chemostratWidth += chemostratColumnWidth;
    }
  }
  overallWidth += chemostratWidth;
  underlayWidth += chemostratWidth;

  let triangleBarTranslate = 0;
  let mainTranslate = 0;

  let triangleBars: any = null;

  if (showTriangleBars && sequenceStratOrder != null) {
    // How many bars are we rendering

    const nOrders = sequenceStratOrder[1] - sequenceStratOrder[0] + 1;
    const paddingInner = 2;
    const triangleBarWidth = 20 * nOrders;

    const triangleUnderlayInset = triangleBarWidth - 10 + 2 * paddingInner;

    let triangleBarInset = 10;
    let triangleBarPadding = 8;
    triangleBarTranslate = 20 * (nOrders - 2);
    underlayWidth -= triangleBarWidth - 10;

    if (triangleBarRightSide) {
      triangleBarPadding = 0;
      triangleBarTranslate += overallWidth - triangleBarInset;
      underlayWidth = overallWidth + 6;
    } else {
      mainTranslate = triangleBarWidth + triangleBarPadding;
      underlayPaddingLeft += triangleUnderlayInset;
    }
    overallWidth += triangleBarWidth + triangleBarPadding;

    triangleBars = h(TriangleBars, {
      id,
      offsetLeft: 0,
      lineWidth: 20,
      minOrder: sequenceStratOrder[0],
      maxOrder: sequenceStratOrder[1],
    });
  }

  const floodingSurfaceStart = 42;

  // Expand SVG past bounds of section

  // We need to change this!

  const grainsizeScaleStart = 40;

  const offset = props.trackerOffset ?? { x: 0, y: 0 };

  return h(
    ColumnBox,
    {
      className: "section-container",
      offsetTop,
      width: overallWidth,
      marginLeft: 0,
      marginRight: 0,
      absolutePosition,
    },
    [
      h("div.section-header", h("h2.section-title", `${id}`)),
      h(
        ColumnTracker,
        {
          className: "section-outer",
          id,
          adjustPosition(pos) {
            // TODO: We need to track down why this is necessary
            const x = pos.x + offset.x;
            const y = pos.y + offset.y + padding.top;
            return new DOMRect(x, y, pos.width, pos.height);
          },
        },
        [
          h(
            GrainsizeLayoutProvider,
            {
              width: innerWidth,
              grainsizeScaleStart,
              grainsizeScaleRange: [grainsizeScaleStart, innerWidth],
            },
            [
              h(EditOverlay, {
                id,
                allowEditing: inEditMode,
                left: padding.left + mainTranslate,
                top: padding.top,
              }),
              h(
                ColumnSVG,
                {
                  width: overallWidth,
                  paddingTop: padding.top,
                  paddingBottom: padding.bottom,
                  paddingLeft: padding.left,
                },
                [
                  h.if(showUnderlay)(ColumnUnderlay, {
                    width: underlayWidth,
                    paddingLeft: underlayPaddingLeft,
                  }),
                  h("g.main", { transform: `translate(${mainTranslate})` }, [
                    h.if(showFloodingSurfaces)(FloodingSurface, {
                      offsetLeft: -floodingSurfaceStart,
                      lineWidth: floodingSurfaceStart,
                    }),
                    h(axisComponent),
                    h(ColumnMain),
                    h(ManagedSymbolColumn, {
                      left: 90,
                      id,
                    }),
                    h.if(isotopesPerSection)(ColumnIsotopes, {
                      id,
                      columnWidth: isotopeColumnWidth,
                      showCarbonIsotopes,
                      showOxygenIsotopes,
                      translateLeft: chemostratColumnStart,
                    }),
                  ]),
                  h(
                    "g.sequence-strat",
                    { transform: `translate(${triangleBarTranslate})` },
                    triangleBars,
                  ),
                ],
              ),
            ],
          ),
          h(
            "div.section-children",
            {
              style: {
                marginTop: padding.top,
                marginLeft: padding.left + mainTranslate,
              },
            },
            [props.children],
          ),
        ],
      ),
    ],
  );
}

const SVGSectionComponent = (props) => {
  const { id, pixelsPerMeter, ...rest } = props;
  return h(
    SummaryColumnProvider,
    { id, pixelsPerMeter },
    h(SVGSectionInner, { id, ...rest }),
  );
};

export { SVGSectionInner, SVGSectionComponent };
